
import GreetTitle from '@/components/common/GreetTitle.vue'
import PhoneCallButton from '@/components/common/PhoneCallButton.vue'
import OrderLocationMap from '@/components/common/OrderLocationMap.vue'
import adTextEmpty from '@/components/common/adTextEmpty.vue'
import OrderTimeline from '@/components/common/OrderTimeline.vue'
import { formatItemQty } from '@/utils/formatUnit'
import { formatTimeChinese, getDateLabel } from '@/utils/time'
import { getCategoryName } from '@/utils/category-dictionary'
import { fetchCustomerService } from '@/utils/customerService'
import { fetchPublicConfig } from '@/utils/publicConfig'
import { resolveOrderLocation } from '@/utils/orderLocation'
import OperatorOrderAction from '@/components/operator/OperatorOrderAction.vue'
import OperatorOrderItem from '@/components/operator/OperatorOrderItem.vue'
import DeliveryConfirmDrawer from '@/components/order/DeliveryConfirmDrawer.vue'
import BaseActionButton from '@/components/common/BaseActionButton.vue'
import OrderItemSpecs from '@/components/common/OrderItemSpecs.vue'
import { openPickupConfirm } from '@/utils/pickupConfirm'
// 对话原语（原生弹窗治理 Phase 1）：本文件 3 处原生弹窗（1 alert + 2 confirm）全部迁到这里
import { showMessage, confirmAction, confirmDanger } from '@/utils/dialog'
import apiConfig from '@/config/api.config'
import {
  ROLES,
  SOURCE_STATUS,
  getTabOptions,
  getDisplayStatus,
  getTabCategory,
  getAvailableActions,
  calculateTabCounts,
  suggestTabByPriority,
  normalizeOrders,
  filterOrdersByTab,
  formatPackingInfo,
  TAB_PRIORITY
} from '@/utils/orderStatusUnified'

// 这些 tab 保持原有「括号数字」显示；其余进行中状态改用 badge 角标
const PAREN_TABS = new Set(['all', 'completed', 'cancelled'])

export default {
  name: 'OrderList',
  components: {
    GreetTitle,
    PhoneCallButton,
    OrderLocationMap,
    adTextEmpty,
    OrderTimeline,
    OperatorOrderAction,
    OperatorOrderItem,
    DeliveryConfirmDrawer,
    BaseActionButton,
    OrderItemSpecs
  },
  data () {
    return {
      loading: true,
      orders: [],
      // 客服电话（canonical 配置）：订单详情"联系我们"统一拨打客服号，未配置时隐藏该入口
      servicePhone: '',
      // 公共配置（自提地址当前值）：resolver 的旧数据 fallback 依据
      publicCfg: null,
      newOrderCount: 0, // 快照轮询发现的新订单数（admin/operator；点击提示条才刷新列表，不自动刷新）
      showDetail: false,
      copiedOrderNo: false, // 详情弹窗订单号已复制（✓ 态 1.6s 自动复位）
      currentOrder: null,
      defaultImage: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%23f0f0f0" width="60" height="60"/%3E%3C/svg%3E',
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      btnX: 0,
      btnY: 0,
      hasMoved: false,
      filterStatus: 'all',
      filterTabIndex: 0,
      filterLock: false,
      userHasSwitchedTab: false,
      collapsedDates: {},
      // 订单金额显隐（Order Amount Visibility）：订单 id → true/false 对象 map（Vue 2 响应式须 $set）。
      // 纯前端本地状态：不写库、不进偏好；刷新/重进默认重新隐藏；每单独立，展开 A 不影响 B
      amountRevealed: {},
      // 条码视图：operator/admin 切换商品明细为内部编码 Code128 条码（ERP 打单/核对）
      scanMode: false,
      barcodeUrls: {},   // product_id → data URL（成功缓存，避免重复请求）
      barcodeFailed: {}, // product_id → true（无 inner_code 或接口失败 → 占位“缺少内部编码”）
      // 送达抽屉相关
      showDeliveryDrawer: false,
      deliveryOrder: null,
      _refreshTimer: null
    }
  },
  computed: {
    // 订单地址统一 resolver：pickup → 快照/当前自提地址；配送 → 收货地址。
    // 文字/地图/导航全部只认这一个结果（严禁三处各自取数出现 A/B/C 三个地址）
    currentLoc () {
      return resolveOrderLocation(this.currentOrder, this.publicCfg)
    },
    currentRole () {
      if (this.$store.getters.isOperator) return ROLES.OPERATOR
      if (this.$store.getters.isCourier) return ROLES.COURIER
      if (this.$store.getters.isAdmin) return ROLES.ADMIN
      return ROLES.USER
    },
    isAdmin () {
      return this.$store.getters.isAdmin
    },
    isCourier () {
      return this.$store.getters.isCourier
    },
    isOperator () {
      return this.$store.getters.isOperator
    },
    userId () { return this.$store.getters.currentUserId },
    isUser () {
      return this.$store.getters.isUser
    },
    // 用户侧公开配送联系（2026-09-22 员工手机号隐私边界）：绝不绑员工手机号（后端已对 user
    // 剥离 delivery_info.phone）——配送公开电话（delivery_contact_phone）优先 → 客服电话
    // fallback；都未配置返回 null（模板整行隐藏，不渲染空 tel:）。内部角色不走此口
    // （直拨员工号，API 仅对内部角色下发）
    publicDeliveryContact () {
      if (!this.isUser) return null
      const dc = (this.publicCfg && this.publicCfg.deliveryContact) || {}
      const phone = ((dc.phone || '').trim()) || ((dc.phoneSecondary || '').trim())
      if (phone) return { phone, label: '联系配送' }
      if (this.servicePhone) return { phone: this.servicePhone, label: '联系商家' }
      return null
    },
    // 送达抽屉的角色只决定 API 前缀(admin/operator→mobile-delivery,courier→courier-mobile)
    deliveryDrawerRole () {
      if (this.isCourier) return 'courier'
      if (this.isOperator) return 'operator'
      return 'admin'
    },
    filterOptions () {
      return getTabOptions(this.currentRole)
    },
    computedTabIndex: {
      get () {
        const idx = this.filterOptions.findIndex(o => o.value === this.filterStatus)
        return idx >= 0 ? idx : 0
      },
      set (val) {
        this.filterStatus = this.filterOptions[val]?.value || 'all'
      }
    },
    pageTitle () {
      if (this.isCourier) return '我的配送'
      if (this.isAdmin) return '订单管理'
      return '我的订单'
    },
    normalizedOrders () {
      const list = normalizeOrders(this.orders, this.currentRole, this.userId)
      // 业务员 tab 按 验货状态/配送负责人 归类（待处理/处理中/配送中/已完成/历史），覆盖 source-status 默认归类
      if (this.isOperator) {
        list.forEach(o => { o._tabCategory = this.opTabOf(o) })
      }
      return list
    },
    filteredOrders () {
      return filterOrdersByTab(this.normalizedOrders, this.filterStatus)
    },
    groupedOrders () {
      const groups = {}
      this.filteredOrders.forEach(o => {
        const date = getDateLabel(o.created_at) || '未知日期'
        if (!groups[date]) groups[date] = []
        groups[date].push(o)
      })
      // 按日期排序：今天 > 昨天 > 其他日期（倒序）
      const dateOrder = { '今天': 1, '昨天': 2 }
      const sortedDates = Object.keys(groups).sort((a, b) => {
        const orderA = dateOrder[a] || 3
        const orderB = dateOrder[b] || 3
        if (orderA !== orderB) return orderA - orderB
        return b.localeCompare(a)
      })
      // 默认展开：今天优先，否则最新日期
      const hasToday = sortedDates.includes('今天')
      const defaultExpandedDate = hasToday ? '今天' : (sortedDates[0] || null)

      const result = sortedDates.map(date => {
        const neverToggled = !(date in this.collapsedDates)
        const defaultExpanded = date === defaultExpandedDate
        return {
          date,
          orders: groups[date],
          collapsed: neverToggled ? !defaultExpanded : this.collapsedDates[date] === true
        }
      })
      return result
    },
    statusCounts () {
      // badge 数量必须与列表筛选同源：operator 用 opTabOf（按负责人字段），其他角色用 calculateTabCounts
      if (this.isOperator) {
        const counts = { all: this.orders.length }
        this.filterOptions.forEach(t => { if (t.value !== 'all') counts[t.value] = 0 })
        this.normalizedOrders.forEach(o => { const c = o._tabCategory; if (counts[c] !== undefined) counts[c]++ })
        return counts
      }
      return calculateTabCounts(this.orders, this.currentRole)
    }
  },
  watch: {
    '$route.query.status' (newStatus) {
      const validStatuses = this.filterOptions.map(opt => opt.value)
      if (newStatus && validStatuses.includes(newStatus)) {
        this.filterStatus = newStatus
      }
    },
    // 关闭订单详情抽屉时重置为普通模式（下次打开默认商品信息视图）
    showDetail (v) {
      if (!v) this.scanMode = false
    },
    // Assistant 订单卡跳转（?open=fo.id）：已在本页时 query 变化也能拉起（列表已加载则直接开）
    '$route.query.open' (v) {
      if (v) {
        this._autoOpenOrderId = String(v)
        this.maybeAutoOpenDetail()
      }
    }
  },
  created () {
    const status = this.$route.query.status
    const validStatuses = this.filterOptions.map(opt => opt.value)
    if (status && validStatuses.includes(status)) {
      this.filterStatus = status
    }
    // Assistant 订单卡入口：?open=fo.id → 列表加载后自动 viewDetail（复用现有详情抽屉）
    if (this.$route.query.open) this._autoOpenOrderId = String(this.$route.query.open)
    this.fetchOrders()
    fetchCustomerService().then(cs => { this.servicePhone = cs.phone })
    // 自提地址当前值：旧数据无 snapshot 时 resolver 的 fallback 依据（新单有快照不依赖）
    fetchPublicConfig().then(cfg => { this.publicCfg = cfg }).catch(() => {})
  },
  methods: {
    formatItemQty,
    categoryText (category) {
      return getCategoryName(category)
    },
    toggleDateCollapse (date) {
      const group = this.groupedOrders.find(g => g.date === date)
      if (!group) return
      this.$set(this.collapsedDates, date, !group.collapsed)
    },
    formatDateLabel (date) {
      // group.date 已经是 getDateLabel 的结果（今天/昨天/YYYY-MM-DD），直接透传
      return date
    },
    setFilter (status) {
      if (this.filterLock) return
      if (this.filterStatus === status) {
        this.filterLock = false
        return
      }
      this.filterLock = true
      this.filterStatus = status
      this.userHasSwitchedTab = true
      const query = status === 'all' ? {} : { status }
      this.$router.replace({ path: '/order', query }).catch(() => {})
      setTimeout(() => { this.filterLock = false }, 300)
    },
    // 操作成功后根据新状态自动切换到对应 tab
    followOrderStatus (newSourceStatus) {
      const tab = getTabCategory(newSourceStatus, this.currentRole)
      if (tab && tab !== 'all') {
        this.setFilter(tab)
      }
    },
    onFilterChange (index) {
      this.setFilter(this.filterOptions[index].value)
    },
    getTabTitle (tab) {
      const count = this.statusCounts[tab.value]
      // 全部 / 已完成 / 已取消：保持「括号数字」现状
      if (PAREN_TABS.has(tab.value)) {
        return count > 0 ? `${tab.label} (${count})` : tab.label
      }
      // 进行中状态：数字改用 badge 角标，标题只保留文字
      return tab.label
    },
    getTabBadge (tab) {
      // 终态 / 全部保持括号，不显示 badge
      if (PAREN_TABS.has(tab.value)) return undefined
      const count = this.statusCounts[tab.value]
      return count > 0 ? count : undefined
    },
    // === 新订单检测（30s 轮询；admin/operator 只比对轻量快照显示提示不自动刷新，user/courier 维持原列表自动刷新） ===
    _startPolling () {
      this._stopPolling()
      if (this.isAdmin || this.isOperator) {
        this._syncSnapshot()
        this._refreshTimer = setInterval(() => {
          if (document.hidden) return
          this.axios.get(this._snapshotUrl()).then(res => {
            if (res.data.code !== 200) return
            const s = res.data.data || {}
            const base = this._snapshot
            // 基准 latest 为 null（空池起步）视作任何新单都更新；注意 JS 里 string>null 恒 false，必须显式判空
            const hasNewer = s.latest_created_at && (!base.latest_created_at || s.latest_created_at > base.latest_created_at)
            if (base && hasNewer && s.total > base.total) {
              this.newOrderCount = s.total - base.total
            }
          }).catch(() => {})
        }, 30000)
      } else {
        this._refreshTimer = setInterval(() => {
          if (!document.hidden) {
            this.fetchOrders()
          }
        }, 30000)
      }
    },
    _snapshotUrl () {
      // 管理员=全部订单；业务员=待接管池（与 processing-pool 同口径，新单只会出现在池里）
      return this.isAdmin ? '/admin/orders-snapshot' : '/operator-mobile/orders-snapshot'
    },
    // 快照基准 = 当前列表已反映的订单现实；仅在列表刷新后更新
    _syncSnapshot () {
      this.axios.get(this._snapshotUrl()).then(res => {
        if (res.data.code === 200) this._snapshot = res.data.data
      }).catch(() => {})
    },
    handleNewOrderClick () {
      this.fetchOrders() // 成功后 newOrderCount 清零 + 基准重置
    },
    _stopPolling () {
      if (this._refreshTimer) {
        clearInterval(this._refreshTimer)
        this._refreshTimer = null
      }
    },
    async fetchOrders () {
      // 过期保护：提示条点击与手动刷新并发时，旧响应晚到不许覆盖新响应（请求序号比对）
      const seq = this._fetchSeq = (this._fetchSeq || 0) + 1
      this.loading = true
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          this.$router.push('/')
          return
        }
        // 业务员（门店业务员）：待接管新单(operator_id IS NULL) + 我的处理任务(operator_id=me) + 我的配送(assigned_courier=me)
        // 点击【开始验货】即接管（写 operator_id=me），无"接单/领取"按钮
        if (this.isOperator) {
          const [pool, mine, deliv] = await Promise.all([
            this.axios.get('/operator-mobile/processing-pool').catch(() => ({ data: { data: [] } })),
            this.axios.get('/operator-mobile/my-tasks').catch(() => ({ data: { data: [] } })),
            this.axios.get('/mobile-delivery/mine').catch(() => ({ data: { data: [] } }))
          ])
          const map = new Map()
          ;(pool.data.data || []).forEach(o => map.set(o.id, { ...o })) // 待接管的新单
          ;(mine.data.data || []).forEach(o => { const e = map.get(o.id); if (e) Object.assign(e, o); else map.set(o.id, { ...o }) })
          ;(deliv.data.data || []).forEach(o => { if (!map.has(o.id)) map.set(o.id, { ...o }) })
          // 已发布到公共配送池且无人负责的单：已移交 courier 池，不进业务员处理视图
          if (seq !== this._fetchSeq) return
          this.orders = Array.from(map.values()).filter(o => !(o.delivery_published_at && !o.assigned_courier_id))
          if (!this.userHasSwitchedTab) this.autoSelectTabByPriority()
          this.maybeAutoOpenDetail()
          // 列表已反映现实 → 新订单提示清零、快照基准重置
          this.newOrderCount = 0
          this._syncSnapshot()
          return
        }
        let url = apiConfig.apiList.getMyOrders
        if (this.isAdmin) {
          url = 'admin-mobile/orders'
        } else if (this.isCourier) {
          url = 'courier-mobile/orders'
        }
        const res = await this.axios({
          url: url,
          method: 'get',
          headers: { Authorization: `Bearer ${token}` }
        })
        if (seq !== this._fetchSeq) return
        if (res.data.code === 200) {
          this.orders = res.data.data || []
          if (!this.userHasSwitchedTab) {
            this.autoSelectTabByPriority()
          }
          this.maybeAutoOpenDetail()
          // 列表已反映现实 → 新订单提示清零、快照基准重置（仅 admin/operator 有快照轮询）
          if (this.isAdmin || this.isOperator) {
            this.newOrderCount = 0
            this._syncSnapshot()
          }
        } else {
          this.orders = []
        }
      } catch (err) {
        console.error('获取订单失败:', err)
        this.orders = []
      } finally {
        this.loading = false
      }
    },
    autoSelectTabByPriority () {
      const urlStatus = this.$route.query.status
      if (urlStatus) return
      const priority = TAB_PRIORITY[this.currentRole] || TAB_PRIORITY[ROLES.USER]
      for (const tabValue of priority) {
        if (this.statusCounts[tabValue] > 0) {
          this.filterStatus = tabValue
          break
        }
      }
    },
    // Assistant 订单卡跳转落点：?open=fo.id → 列表加载后自动拉起现有详情抽屉（viewDetail 复用，
    // 不新造 Assistant 专用详情）；一次性消费，找得到才开（找不到静默留在列表）
    maybeAutoOpenDetail () {
      const id = this._autoOpenOrderId
      if (!id) return
      const target = (this.orders || []).find(o => o && o.id === id)
      if (target) {
        this._autoOpenOrderId = null
        this.viewDetail(target)
      }
    },
    async viewDetail (order) {
      this.currentOrder = order
      this.showDetail = true
      // 金额 reveal 是「本次进入详情」的瞬态（2026-09-18 修正：此前卡片上/上次详情里点开的
      // reveal 会带进本次详情，表现为部分订单详情默认展开）：每次打开详情，本单一律回默认遮挡；
      // 同一 state map 上的列表卡片随之同步回 ****。admin 直显走 isAdmin 分支，不受影响
      this.$set(this.amountRevealed, order.id, false)
      // 所有角色统一走 /orders/:id（fo 详情，对象级权限 + 金额隔离 + events/timeline）
      try {
        const token = localStorage.getItem('token')
        const res = await this.axios({
          url: `${apiConfig.apiList.getOrderDetail}/${order.id}`,
          method: 'get',
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.data.code === 200) {
          const detailData = res.data.data
          const normalized = normalizeOrders([{ ...order, ...detailData }], this.currentRole, this.userId)
          this.currentOrder = normalized[0]
        }
      } catch (err) {
        console.log('使用本地订单数据', err)
      }
    },
    // 切换条码视图：首次开启时批量加载当前订单商品条码（barcodeUrls 缓存，同商品不重复请求）
    toggleScanMode () {
      this.scanMode = true
      if (this.currentOrder && this.currentOrder.items) {
        this.currentOrder.items.forEach(item => this.loadBarcode(item))
      }
    },
    async loadBarcode (item) {
      const pid = item.product_id
      if (!pid) { this.$set(this.barcodeFailed, pid, true); return }
      // 缓存：已成功或已失败均不重试
      if (this.barcodeUrls[pid] || this.barcodeFailed[pid]) return
      try {
        const token = localStorage.getItem('token')
        const res = await this.axios.get(`/products/${pid}/barcode`, {
          params: { format: 'svg' },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'text'
        })
        if (typeof res.data === 'string' && res.data.includes('<svg')) {
          this.$set(this.barcodeUrls, pid,
            'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(res.data))))
        } else {
          this.$set(this.barcodeFailed, pid, true)
        }
      } catch (e) {
        // 400 无 inner_code / 404 商品删 → 占位“缺少内部编码”（不隐藏商品，保持卡片高度）
        this.$set(this.barcodeFailed, pid, true)
      }
    },
    // 业务员 tab 归类（仅 operator 用）：待处理=验货未开始；处理中=验货中/验完待选配送；配送中=我配送或已交他人；已完成/历史
    // operator tab 归类（按负责人字段，不按角色复制）：与 statusCounts/badge 同源，避免数量与列表不一致
    opTabOf (o) {
      if (o.status === 'cancelled') return 'cancelled'
      if (['delivered', 'confirmed'].includes(o.status)) return 'completed'
      const me = this.userId
      if (o.assigned_courier_id === me) return 'delivering' // 我负责配送
      if (o.operator_id === me && o.assigned_courier_id && o.assigned_courier_id !== me) return 'delivering' // 我处理、已交他人配送（只读）
      if (o.operator_id === me && !o.assigned_courier_id) return 'processing' // 处理中（验货中/验完待选配送）
      if (!o.operator_id && !o.assigned_courier_id) return 'pending' // 无人处理的新单（待接管）
      return 'all'
    },
    // 业务员动作触发后：刷新列表，并把详情当前订单替换为最新行
    opDetailRefresh () {
      this.fetchOrders().then(() => {
        if (this.currentOrder) {
          const fresh = this.orders.find(o => o.id === this.currentOrder.id)
          if (fresh) {
            this.currentOrder = fresh
            // 操作后跟随订单新状态切换 tab
            const newStatus = fresh._sourceStatus || fresh.status
            this.followOrderStatus(newStatus)
          }
        }
      })
    },
    getPreviewImages (order) {
      if (order.preview_images && order.preview_images.length > 0) {
        const images = order.preview_images
          .filter(img => img && img !== 'null' && img !== 'undefined')
          .map(img => {
            const processed = this.$img(img)
            return processed || this.defaultImage
          })
        if (images.length > 0) return images
      }
      // fallback: preview_images 空（商品无图）→ 用 defaultImage 按商品数量占位
      const count = Math.min(order.items?.length || order.item_count || 1, 5)
      return Array.from({ length: count }, () => this.defaultImage)
    },
    statusText (orderOrStatus) {
      if (orderOrStatus && typeof orderOrStatus === 'object') {
        if (orderOrStatus._displayStatus) {
          return orderOrStatus._displayStatus
        }
        return getDisplayStatus(orderOrStatus.status || orderOrStatus._sourceStatus, this.currentRole, orderOrStatus.fulfillment_mode)
      }
      return getDisplayStatus(orderOrStatus, this.currentRole)
    },
    formatDate (date) {
      return formatTimeChinese(date)
    },
    // 复制订单号（AssistantOrderCard.copyNo 同款降级链：Clipboard API → execCommand 兜底 → alert）
    async copyOrderNo () {
      const no = String(this.currentOrder ? (this.currentOrder.order_no || this.currentOrder.id) : '')
      if (!no || this.copiedOrderNo) return
      let ok = false
      try {
        await navigator.clipboard.writeText(no)
        ok = true
      } catch (_) {
        // Clipboard API 不可用（旧内核/非安全上下文）→ execCommand 兜底；再失败 → 项目既有 alert 回退
        try {
          const ta = document.createElement('textarea')
          ta.value = no
          ta.setAttribute('readonly', '')
          ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0'
          document.body.appendChild(ta)
          ta.select()
          ok = document.execCommand('copy')
          document.body.removeChild(ta)
        } catch (_) { ok = false }
      }
      // A 类（纯告知）：复制失败只是「这条路走不通」，不需要用户做决定 → Notify，不弹窗
      if (!ok) { showMessage('复制失败，请手动选择', { type: 'negative' }); return }
      this.copiedOrderNo = true
      clearTimeout(this._copyNoTimer)
      this._copyNoTimer = setTimeout(() => { this.copiedOrderNo = false }, 1600)
    },
    onRefreshClick () {
      if (this.hasMoved) return
      this.fetchOrders()
    },
    startDrag (e) {
      this.isDragging = true
      this.hasMoved = false
      const touch = e.touches ? e.touches[0] : e
      this.dragStartX = touch.clientX
      this.dragStartY = touch.clientY
      const btn = this.$refs.refreshBtn
      const rect = btn.getBoundingClientRect()
      this.btnX = rect.left
      this.btnY = rect.top
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.endDrag)
      document.addEventListener('touchmove', this.onDrag)
      document.addEventListener('touchend', this.endDrag)
    },
    onDrag (e) {
      if (!this.isDragging) return
      e.preventDefault()
      const touch = e.touches ? e.touches[0] : e
      const dx = touch.clientX - this.dragStartX
      const dy = touch.clientY - this.dragStartY
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        this.hasMoved = true
      }
      const btn = this.$refs.refreshBtn
      btn.style.left = (this.btnX + dx) + 'px'
      btn.style.top = (this.btnY + dy) + 'px'
      btn.style.right = 'auto'
      btn.style.bottom = 'auto'
    },
    endDrag () {
      if (!this.isDragging) return
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.endDrag)
      document.removeEventListener('touchmove', this.onDrag)
      document.removeEventListener('touchend', this.endDrag)
      const btn = this.$refs.refreshBtn
      const rect = btn.getBoundingClientRect()
      const winW = window.innerWidth
      const winH = window.innerHeight
      const margin = 20
      const btnW = 50
      const btnH = 50
      const distLeft = rect.left
      const distRight = winW - rect.right
      const distTop = rect.top
      const distBottom = winH - rect.bottom
      const minDist = Math.min(distLeft, distRight, distTop, distBottom)
      btn.style.transition = 'all 0.3s ease'
      if (minDist === distLeft) {
        btn.style.left = margin + 'px'
        btn.style.top = rect.top + 'px'
      } else if (minDist === distRight) {
        btn.style.left = (winW - margin - btnW) + 'px'
        btn.style.top = rect.top + 'px'
      } else if (minDist === distTop) {
        btn.style.left = rect.left + 'px'
        btn.style.top = margin + 'px'
      } else {
        btn.style.left = rect.left + 'px'
        btn.style.top = (winH - margin - btnH) + 'px'
      }
      setTimeout(() => {
        btn.style.transition = ''
      }, 300)
    },
    inspectionStatusText (status) {
      const map = {
        pending: '待验货',
        in_progress: '验货中',
        completed: '已验货',
        abnormal: '异常',
        skipped: '已跳过'
      }
      return map[status] || ''
    },
    // 商品级验货结果（canonical = order_items.inspection_status，与 operator 端 OperatorOrderItem 同一来源）
    itemInspectionText (item) {
      const map = { pending: '待验货', passed: '通过', abnormal: '异常', in_progress: '验货中' }
      return map[item && item.inspection_status] || ''
    },
    itemInspectionClass (item) {
      const map = { pending: 'wait', passed: 'ok', abnormal: 'bad', in_progress: 'doing' }
      return map[item && item.inspection_status] || ''
    },
    // 开始验货：admin 走独立验货页（/order/:id/inspection）；operator 由其 action 组件处理
    startInspection (order) {
      this.$router.push(`/order/${order.id}/inspection`)
    },
    assignCourier (order) {
      this.$router.push(`/order/${order.id}/assign`)
    },
    shipOrder (order) {
      this.$router.push(`/order/${order.id}/ship`)
    },
    async assignCourierSelf (order) {
      const myId = this.$store.getters.currentUserId
      try { await this.axios.post(`/admin-mobile/orders/${order.id}/assign`, { courier_id: myId }) }
      catch (e) { this.$q.notify({ type: 'negative', message: '指派失败' }); return }
      try {
        await this.axios.post(`/mobile-delivery/orders/${order.id}/start`)
        this.showDetail = false
        this.$q.notify({ type: 'positive', message: '已指派自己并开始配送' })
        this.followOrderStatus(SOURCE_STATUS.DELIVERING)
      } catch (e) {
        // start 失败 → 回滚自己刚造成的指派：复用正式 release 接口（对象级 CAS，
        // 只放回 assigned_courier_id=自己 的单，绝不误清他人接手；配送已开始则 400 拒绝）。
        // 旧实现发 {courier_id:''} 给 assign → 400 缺少 courier_id 被吞，回滚从未生效。
        let rolled = false
        try { await this.axios.post(`/mobile-delivery/tasks/${order.id}/release`); rolled = true } catch (err) { console.error('回滚指派失败（订单状态可能已变化）', err) }
        this.$q.notify({ type: 'negative', message: rolled ? '开始配送失败，已取消指派' : '开始配送失败，订单状态可能已变化，请刷新后重试' })
      }
      this.fetchOrders()
    },
    async adminStartDelivery (order) {
      try {
        await this.axios.post(`/mobile-delivery/orders/${order.id}/start`)
        this.$q.notify({ type: 'positive', message: '已开始配送' })
        this.followOrderStatus(SOURCE_STATUS.DELIVERING)
        this.fetchOrders()
      } catch (err) {
        this.$q.notify({ type: 'negative', message: err.response?.data?.message || '操作失败' })
      }
    },
    async startDelivery (order) {
      try {
        await this.axios.post(`/courier-mobile/orders/${order.id}/start`)
        this.$q.notify({ type: 'positive', message: '已开始配送' })
        this.followOrderStatus(SOURCE_STATUS.DELIVERING)
        this.fetchOrders()
        if (this.currentOrder && this.currentOrder.id === order.id) {
          this.viewDetail(order)
        }
      } catch (err) {
        this.$q.notify({ type: 'negative', message: err.response?.data?.message || '操作失败' })
      }
    },
    async markDelivered (order) {
      // 关闭订单详情抽屉，避免嵌套双抽屉问题
      this.showDetail = false

      // 打开统一送达确认抽屉(照片/备注/提交/loading 全部内聚在 DeliveryConfirmDrawer)
      this.deliveryOrder = order
      this.showDeliveryDrawer = true
    },
    // 送达成功回调:跟进状态 + 刷新列表(通知与关抽屉由抽屉组件自理)
    onDeliverySuccess () {
      this.deliveryOrder = null
      this.followOrderStatus(SOURCE_STATUS.DELIVERED)
      this.fetchOrders()
      // 送达完成后不重新打开订单详情，保持在订单列表
    },
    formatPackingInfoDisplay (packingInfo) {
      return formatPackingInfo(packingInfo)
    },
    // 改为自提（用户端入口：仅未进入配送履约前可改；后端校验 pending_confirm，生成自提码）
    // 迁移（原生弹窗治理 Phase 1）：window.confirm → confirmAction。
    // **语义继承**：attention（橙），不是危险红——本动作不可逆但不破坏数据，且触发它的按钮
    // 在仓内已定为 semantic="attention"（见模板「改为自提」），弹窗必须同语义，不得改判成红。
    // 请求放进弹窗内执行（数据自持）：loading 期间键 disabled（防双击重复提交），
    // 失败留在弹窗里显示原因、不静默关掉。
    async switchToPickup (order) {
      await confirmAction({
        parent: this,
        title: '改为到店自提',
        message: '确认将这单改为到店自提？',
        consequence: '更改后将凭自提码到店取货，不再配送；已切换的履约方式无法改回。',
        okLabel: '改为自提',
        semantic: 'attention',
        persistent: true, // 不可逆动作：点遮罩/ESC 不关，必须明确选取消或确认
        action: async () => {
          const res = await this.axios.post(`/admin-mobile/orders/${order.id}/set-pickup`, {})
          if (!res.data || res.data.code !== 200) {
            throw new Error((res.data && res.data.message) || '操作失败')
          }
          this.$q.notify({ type: 'positive', message: res.data.message || '已改为自提' })
          this.showDetail = false
          this.fetchOrders()
        }
      })
    },
    // 确认已自提（自提单完成动作：等价标记送达，直达已完成，不等用户确认）
    // 2026-09-24：window.prompt 换成 Quasar 弹窗（展示本单自提码供核对）；请求/错误都在弹窗内，
    // 成功后才由本处收起详情并刷新列表——失败保留弹窗，不静默丢弃店员的操作。
    confirmPickup (order) {
      openPickupConfirm(this, order, () => {
        this.showDetail = false
        this.fetchOrders()
      })
    },
    productSummary (order) {
      const items = order.items
      if (items && items.length > 0) {
        const maxLen = 40
        let text = items
          .map(i => `${i.product_name} ×${i.quantity || 1}`)
          .join('、')
        if (text.length > maxLen) {
          text = text.slice(0, maxLen) + '...'
        }
        return text
      }
      return `${order.item_count || 0} 种商品，共 ${order.total_quantity || 0} 件`
    },
    // ===== 订单金额显隐（Order Amount Visibility）=====
    // 权限唯一来源是后端 DTO 裁剪：total_amount 在 = 可见总额；item.unit_price 在 = 可见单价/小计。
    // 有权限未 reveal → 前端视觉遮挡（**** / 留白占位）；无权限 → 字段不在，前端不渲染任何金额区（无虚假交互）。
    // 金额区即交互区（无按钮无图标）：点订单总金额行 / 商品行价格区 / 实际金额行，统一切换本单 reveal
    toggleAmountReveal (orderId) {
      // 一单一 state（对象 map + $set 保证 Vue 2 响应式，Set 不可用）；纯本地，刷新即重置
      this.$set(this.amountRevealed, orderId, !this.amountRevealed[orderId])
    },
    fmtAmt (v) {
      return Number(v || 0).toFixed(2)
    },
    async confirmReceipt (order) {
      try {
        const res = await this.axios.post(`/orders/${order.id}/confirm`)
        if (res.data.code === 200) {
          this.$q.notify({ type: 'positive', message: '确认收货成功' })
          this.showDetail = false
          this.followOrderStatus(SOURCE_STATUS.CONFIRMED)
          this.fetchOrders()
        }
      } catch (err) {
        this.$q.notify({ type: 'negative', message: err.response?.data?.message || '操作失败' })
      }
    },
    canCancelOrder (order) {
      if (!order) return false
      const s = order._sourceStatus || order.status
      return s === 'pending_confirm' || s === 'picked'
    },
    // 注意：用户自助取消已下线（模板无入口，仅 canCancelOrder 判定保留），本方法当前不可达；
    // 仍按 C 类迁移（确认取消订单是不可逆业务态切换，无回退入口 → 危险确认 + 后果文案）。
    async cancelOrder (order) {
      await confirmDanger({
        parent: this,
        title: '取消订单',
        message: '确认取消此订单？',
        consequence: '取消后不可恢复：订单进入已取消终态，商品不再履约。',
        okLabel: '确认取消',
        action: async () => {
          const res = await this.axios.post(`/orders/${order.id}/cancel`, { reason: '用户主动取消' })
          if (!res.data || res.data.code !== 200) {
            throw new Error((res.data && res.data.message) || '取消失败')
          }
          this.$q.notify({ type: 'positive', message: '订单已取消' })
          this.showDetail = false
          this.followOrderStatus(SOURCE_STATUS.CANCELLED)
          this.fetchOrders()
        }
      })
    }
  },
  mounted () {
    const btn = this.$refs.refreshBtn
    if (btn) {
      btn.style.left = (window.innerWidth - 70) + 'px'
      btn.style.top = (window.innerHeight - 150) + 'px'
      btn.style.right = 'auto'
      btn.style.bottom = 'auto'
    }
    this._startPolling()
  },
  beforeDestroy () {
    this._stopPolling()
    document.removeEventListener('mousemove', this.onDrag)
    document.removeEventListener('mouseup', this.endDrag)
    document.removeEventListener('touchmove', this.onDrag)
    document.removeEventListener('touchend', this.endDrag)
  }
}
